import type { MouseEventHandler, PropsWithChildren } from 'react';
import styled, { css, useTheme } from 'styled-components';

import type { SEUKTheme } from '@seuk/design-system/theme';

import { Icon } from '../../atoms/icon';

export type MessageType = 'info' | 'warning' | 'danger';

export interface MessageProps {
  type: MessageType;
  className?: string;
  isCloseButtonShown?: boolean;
  onCloseButtonClick?: () => void;
}

const getBackgroundColor = (type: MessageType, theme: SEUKTheme) => {
  switch (type) {
    case 'warning':
      return theme.palette.accent.primary.medium;
    case 'danger':
      return theme.palette.accent.secondary.strong;
    default:
      return theme.palette.focus.medium;
  }
};

const Container = styled.div<{ $type: MessageType }>`
  width: 100%;
  display: flex;
  justify-content: space-evenly;
  background: ${({ $type, theme }) => getBackgroundColor($type, theme)};

  ${({ theme }) => theme.breakpoints.up('lg')} {
    border-radius: ${({ theme }) => theme.radius};
    overflow: hidden;
  }
`;

const getIconBackgroundColor = (type: MessageType, theme: SEUKTheme) => {
  switch (type) {
    case 'warning':
      return theme.palette.system.subtle.warning;
    case 'danger':
      return theme.palette.system.subtle.negative;
    default:
      return theme.palette.accent.tertiary.medium;
  }
};

const IconContainer = styled.div<{ $type: MessageType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: ${({ theme }) => theme.spacing(15)};
  padding: ${({ theme }) => theme.spacing(5)};
  box-sizing: border-box;
  background: ${({ $type, theme }) => getIconBackgroundColor($type, theme)};
`;

const TextContainer = styled.div<{ $type: MessageType }>`
  ${({ theme, $type }) => css`
    ${theme.typography.paragraph.medium.normal};
    display: flex;
    align-items: center;
    padding: ${theme.spacing(5)};
    flex: 1;
    color: ${$type === 'danger' ? theme.palette.background.surface : theme.palette.text.onSurface.strong};
  `}
`;

const CloseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.spacing(15)};
  padding: ${({ theme }) => theme.spacing(5)};
  box-sizing: border-box;
  ${({ theme }) => theme.typography.label.extraSmall};
  border: none;
  border-radius: 0;
  cursor: pointer;
  background: inherit;
`;

const getIconNameByType = (type: MessageType) => {
  switch (type) {
    case 'warning':
      return 'Warning';
    case 'danger':
      return 'Danger';
    default:
      return 'Info';
  }
};

export const Message = ({
  type,
  isCloseButtonShown,
  children,
  onCloseButtonClick,
  className,
}: PropsWithChildren<MessageProps>) => {
  const theme = useTheme();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();

    onCloseButtonClick?.();
  };

  const iconName = getIconNameByType(type);

  return (
    <Container $type={type} className={className}>
      <IconContainer $type={type}>
        <Icon
          name={iconName}
          color={theme.palette.text.onSurface.strong}
          width="20"
          height="20"
          aria-label={`${iconName} icon`}
        />
      </IconContainer>
      <TextContainer $type={type}>{children}</TextContainer>
      {isCloseButtonShown === true ? (
        <CloseButton onClick={handleClick} type="button" aria-label="Close">
          <Icon
            name="Close"
            width="12"
            height="12"
            color={type === 'danger' ? theme.palette.background.surface : theme.palette.text.onSurface.strong}
          />
        </CloseButton>
      ) : null}
    </Container>
  );
};
